import { DOCUMENT } from '@angular/common';
import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, effect, inject, signal } from '@angular/core';
import { HostListener } from '@angular/core';
import { Renderer2, RendererFactory2 } from '@angular/core';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { FileAssetPreviewService } from '@core/services/file-asset-preview.service';
import { TranslatePipe } from '@shared/pipes/translate.pipe';

@Component({
  selector: 'app-file-preview-modal',
  standalone: true,
  imports: [CommonModule, NzModalModule, NzSpinModule, NzEmptyModule, NzButtonModule, NzIconModule, TranslatePipe],
  templateUrl: './file-preview-modal.component.html',
  styleUrls: ['./file-preview-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilePreviewModalComponent {
  protected readonly preview = inject(FileAssetPreviewService);
  protected readonly zoomScale = signal(1);
  protected readonly zoomOrigin = signal('50% 50%');
  private readonly document = inject(DOCUMENT);
  private readonly renderer: Renderer2;
  private readonly minZoom = 0.5;
  private readonly maxZoom = 3;
  private readonly zoomStep = 0.25;

  constructor() {
    const rendererFactory = inject(RendererFactory2);
    this.renderer = rendererFactory.createRenderer(null, null);

    effect(() => {
      this.preview.previewEntry();
      this.zoomScale.set(1);
      this.zoomOrigin.set('50% 50%');
    });
  }

  openExternal(): void {
    const url = this.preview.previewUrl();
    if (!url) {
      return;
    }

    this.clickHiddenAnchor(url, undefined, true);
  }

  download(): void {
    const entry = this.preview.previewEntry();
    const url = this.preview.previewUrl();
    if (!entry || !url) {
      return;
    }

    this.clickHiddenAnchor(url, entry.fileName, true);
  }

  retry(): void {
    this.preview.retryPreview();
  }

  zoomIn(): void {
    this.setZoom(this.zoomScale() + this.zoomStep);
  }

  zoomOut(): void {
    this.setZoom(this.zoomScale() - this.zoomStep);
  }

  resetZoom(): void {
    this.zoomScale.set(1);
    this.zoomOrigin.set('50% 50%');
  }

  canZoomOut(): boolean {
    return this.zoomScale() > this.minZoom;
  }

  canZoomIn(): boolean {
    return this.zoomScale() < this.maxZoom;
  }

  onImageWheel(event: WheelEvent): void {
    if (!this.preview.previewModalVisible()) {
      return;
    }

    if (!this.preview.isPreviewImageEntry(this.preview.previewEntry())) {
      return;
    }

    if (!event.ctrlKey && !event.metaKey) {
      return;
    }

    this.updateZoomOriginFromPointer(event);

    event.preventDefault();
    if (event.deltaY < 0) {
      this.setZoom(this.zoomScale() + 0.1);
      return;
    }

    this.setZoom(this.zoomScale() - 0.1);
  }

  zoomPercentLabel(): string {
    return `${Math.round(this.zoomScale() * 100)}%`;
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.preview.previewModalVisible()) {
      return;
    }

    if (this.isTypingTarget(event.target)) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.preview.closePreview();
      return;
    }

    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      this.zoomIn();
      return;
    }

    if (event.key === '-' || event.key === '_') {
      event.preventDefault();
      this.zoomOut();
      return;
    }

    if (event.key === '0') {
      event.preventDefault();
      this.resetZoom();
    }
  }

  private setZoom(next: number): void {
    const clamped = Math.min(this.maxZoom, Math.max(this.minZoom, Number(next.toFixed(2))));
    this.zoomScale.set(clamped);
  }

  private updateZoomOriginFromPointer(event: WheelEvent): void {
    const stage = event.currentTarget as HTMLElement | null;
    if (!stage) {
      return;
    }

    const image = stage.querySelector('.preview-image') as HTMLImageElement | null;
    if (!image) {
      return;
    }

    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }

    const xRatio = (event.clientX - rect.left) / rect.width;
    const yRatio = (event.clientY - rect.top) / rect.height;
    const x = Math.min(1, Math.max(0, xRatio));
    const y = Math.min(1, Math.max(0, yRatio));
    this.zoomOrigin.set(`${(x * 100).toFixed(2)}% ${(y * 100).toFixed(2)}%`);
  }

  private isTypingTarget(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null;
    if (!element) {
      return false;
    }

    const tagName = element.tagName.toLowerCase();
    return tagName === 'input' || tagName === 'textarea' || element.isContentEditable;
  }

  private clickHiddenAnchor(url: string, downloadName?: string, newTab = false): void {
    const anchor = this.renderer.createElement('a') as HTMLAnchorElement;
    this.renderer.setAttribute(anchor, 'href', url);
    if (downloadName) {
      this.renderer.setAttribute(anchor, 'download', downloadName);
    }
    if (newTab) {
      this.renderer.setAttribute(anchor, 'target', '_blank');
      this.renderer.setAttribute(anchor, 'rel', 'noopener noreferrer');
    }
    this.renderer.setStyle(anchor, 'display', 'none');
    this.renderer.appendChild(this.document.body, anchor);
    anchor.click();
    this.renderer.removeChild(this.document.body, anchor);
  }
}